// React imports
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// Components
import Button from "../components/Button.tsx";

// Contextes
import { useGame } from "../utils/GameContexte.tsx";
import { useInTheGame } from "../utils/InTheGameContexte.tsx";
import { useQuestions } from "../utils/QuestionsContexte.tsx";

// Icons
import { FaFaceSmile, FaFaceFrown } from "react-icons/fa6";
import union from "/union.png";
import unionRed from "/union-red.png";

// Librairies
import gsap from "gsap";

//Interface for the question object
interface Choix {
  choix1: string;
  choix2: string;
  choix3?: string;
  choix4?: string;
}

interface Question {
  statement: string;
  choix?: Choix;
  bonneReponse: string;
  reponseInfos?: string;
  reponsePlus?: string;
  contenuEtoile?: string[];
  questionContenuEtoile?: string[];
  contenuChoixEtoile: string;
}

function Questions() {
  // Question count
  const location = useLocation();
  const { id } = useParams();

  const navigate = useNavigate();
  // Game context
  const {
    incrementPoints,
    totalQuestions,
    questionNumber,
    setGameFinished,
    totalPoints,
    setActivePlayer,
    totalPlayers,
    equipesPoints,
    setEquipesPoints,
    setWinner,
  } = useGame();

  // Extract theme and difficulty from location state
  const questionTheme = location.state
    ? (location.state.theme as string) || ""
    : "";
  const questionDifficulty = location.state
    ? (location.state.difficulty as number) || 0
    : 0;

  // Extract question details from location state
  const question = location.state
    ? (location.state as { question: Question }).question
    : undefined;

  // State for the answer button
  const [hasAnswered, setHasAnswered] = useState(false);

  // State for the answer text
  const [selectAnswerButton, setSelectAnswerButton] = useState<boolean>(false);
  /* const [showInfosText, setShowInfosText] = useState<boolean>(false); */
  const [showAnswerText, setShowAnswerText] = useState<boolean>(false);
  const [showPointsCheck, setShowPointsCheck] = useState("");

  // Used questions saved in the context
  const { resetTeamUsedQuestions } = useQuestions();

  // Toggle Game
  const { toggleGame } = useInTheGame();

  const contentContainerQuestions = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    toggleGame(true);

    const buttonStart = document.querySelector(".button--animated");
    const pointsLogo = document.querySelector(".points--animated");

    const tl = gsap.timeline();

    if (buttonStart && contentContainerQuestions.current) {
      const elements =
        contentContainerQuestions.current.querySelectorAll(".animation-item");

      window.setTimeout(() => {
        tl.to(
          buttonStart,

          {
            autoAlpha: 1,
            opacity: 1,
            xPercent: 100,
            duration: 0.5,
            ease: "power3.out",
          }
        );
        tl.to(
          pointsLogo,

          {
            autoAlpha: 1,
            opacity: 1,
            xPercent: -65,
            duration: 0.5,
            ease: "power3.out",
          },
          "<"
        );
        tl.to(
          elements,
          {
            autoAlpha: 1,
            duration: 1,
            /* stagger: 0.1, */
            ease: "power2.out",
          },
          "<"
        );
      }, 200);
    }
  }, [toggleGame]);

  // On increment le numéro de la question
  const { nextQuestion, activePlayer } = useGame();

  // Définir la couleur du thème
  let themeColor = "";
  if (questionTheme === "La maladie") {
    themeColor = "--maladie";
  } else if (questionTheme === "Les modes de transmission") {
    themeColor = "--transmission";
  } else if (questionTheme === "La prise en charge") {
    themeColor = "--charge";
  } else if (questionTheme === "Le dépistage") {
    themeColor = "--depistage";
  } else {
    themeColor = "--guerison";
  }
  useEffect(() => {
    if (selectAnswerButton) {
      const buttonCheck = document.querySelectorAll(".checkbutton--animated");
      if (buttonCheck.length > 0) {
        gsap.set(buttonCheck, {
          xPercent: 100,
          opacity: 0,
        });

        gsap.to(buttonCheck, {
          xPercent: 0,
          opacity: 1,
          duration: 0.5,
          ease: "power3.out",
        });
      }
    }
  }, [selectAnswerButton]); // Dependency array ensures this runs only when selectAnswerButton changes

  // Handle answer click
  const handleAnswerClick = () => {
    setSelectAnswerButton(true);

    if (!question?.choix) {
      setShowAnswerText(true);
    }
  };

  // Fonction pour gérer la fin du jeu
  useEffect(() => {
    const checkIfGameShouldEnd = () => {
      if (totalPlayers === 1) {
        if (
          (id === "entrainement" && questionNumber >= totalQuestions) ||
          (id === "classique" && totalPoints >= 20) ||
          (id === "expert" && totalPoints >= 20)
        ) {
          if (hasAnswered) {
            setGameFinished(true);
            // Réinitialiser les questions utilisées
            resetTeamUsedQuestions(activePlayer);
            setTimeout(() => {
              navigate(`/${id}/fin`);
            }, 2500);

            return true;
          }
        }
      } else {
        const teamWith20PointsIndex = equipesPoints.findIndex(
          (points) => points >= 20
        );
        if (teamWith20PointsIndex !== -1) {
          if (hasAnswered) {
            setGameFinished(true);
            // Réinitialiser les questions utilisées
            resetTeamUsedQuestions(teamWith20PointsIndex);
            equipesPoints.map((index) => resetTeamUsedQuestions(index));

            setWinner(teamWith20PointsIndex);

            setTimeout(() => {
              navigate(`/${id}/fin`);
            }, 2500);

            return true;
          }
        }
      }
      return false;
    };

    if (!checkIfGameShouldEnd() && hasAnswered) {
      // Si le jeu ne se termine pas et que l'utilisateur a déjà répondu, naviguez vers la prochaine question
      const timer = setTimeout(() => {
        if (totalPlayers > 1) {
          if (activePlayer < totalPlayers - 1) {
            setActivePlayer(activePlayer + 1);
          } else {
            setActivePlayer(0);
            nextQuestion();
          }
        } else {
          nextQuestion();
        }
        navigate(`/${id}/jeu`);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [totalPoints, questionNumber, hasAnswered]);

  // Gérer le clic sur les réponses

  const handleAnswer = useCallback(
    (correct: boolean) => {
      if (!hasAnswered) {
        if (correct) {
          if (totalPlayers > 1) {
            const currentPoints = [...equipesPoints];
            const newPoints = currentPoints.map((points, index) =>
              index === activePlayer ? points + questionDifficulty : points
            );
            setEquipesPoints(newPoints);
          } else {
            incrementPoints(questionDifficulty);
          }
          setShowPointsCheck("yes");
          setImagePosition({
            top: -48,
            left: 60,
          });
        } else {
          setShowPointsCheck("no");
          setImagePosition({
            top: -48,
            left: 11.2,
          });
        }
        setHasAnswered(true);
      }
    },
    [
      hasAnswered,
      totalPlayers,
      equipesPoints,
      activePlayer,
      questionDifficulty,
      incrementPoints,
      setEquipesPoints,
    ]
  ); // Include all dependencies here

  useEffect(() => {
    const buttonCheck = document.querySelectorAll(".pointsCheck");
    const pElements = document.querySelectorAll(".pointsCheck p");
    if (buttonCheck.length > 0) {
      gsap.set([...buttonCheck, ...pElements], { autoAlpha: 0 });

      const tl = gsap.timeline({ ease: "power3.out" });
      tl.to(buttonCheck, {
        autoAlpha: 1,
        duration: 0.5,
      }).to(
        pElements,
        {
          autoAlpha: 1,
          duration: 0.5,
        },
        "-=0.25"
      );
    }
  }, [handleAnswer]); // Dependency array ensures this runs only when selectAnswerButton changes

  // Bouttons
  const [imagePosition, setImagePosition] = useState({ top: 0, left: 0 });

  return (
    <div className="questionsText" ref={contentContainerQuestions}>
      <div>
        <p
          className={`text-infos-light text-infos-light--blue questionsText__info ${
            selectAnswerButton && "questionsText__info--hidden"
          } ${
            id === "expert" ? "questionsText__info--expert" : ""
          } animation-item`}
        >
          Répondez à voix haute puis vérifiez en cliquant sur « voir la réponse
          ».
        </p>

        <h1 className={`questionsText${themeColor} animation-item`}>
          {questionTheme}
          <span className={`questionsPoints${themeColor} points--animated `}>
            {`${questionDifficulty} pt${questionDifficulty > 1 ? "s" : ""}`}
          </span>
        </h1>
        <p className="text-page-semib text-page-semib--black questionsText--alignLeft questionsText--margin animation-item">
          {question?.statement}
        </p>
        {question?.questionContenuEtoile && (
          <ul>
            {question?.questionContenuEtoile.map((etoile, index) => (
              <li
                key={index}
                className="text-page-light text-page-light--blackMedium questionsText__italic questionsText__listItem animation-item"
              >
                {etoile}
              </li>
            ))}
          </ul>
        )}
        {question?.choix && (
          <ol
            className="questionsText--alignLeft text-page-light--black animation-item"
            type="A"
            style={{ marginBottom: "0rem", marginTop: "0rem" }}
          >
            {Object.values(question.choix).map((answer, index) => (
              <li
                key={index}
                className={`questionsText__list ${
                  selectAnswerButton && question.bonneReponse === answer
                    ? "text-page-bold text-page-bold--green"
                    : ""
                }`}
              >
                {answer}
                {selectAnswerButton &&
                  question.bonneReponse === answer &&
                  question?.reponseInfos && (
                    <>
                      <i className="text-page-bold-small">
                        {question?.reponseInfos}
                      </i>
                    </>
                  )}
              </li>
            ))}
          </ol>
        )}
        {question?.contenuChoixEtoile && (
          <p className="text-page-light text-page-light--blackMedium questionsText__contenuChoixEtoile animation-item">
            {question.contenuChoixEtoile}
          </p>
        )}
        {showAnswerText && (
          <p className="text-page-bold text-page-bold--green questionsText--alignLeft questionsText--margin ">
            {showAnswerText && question?.bonneReponse}
            {showAnswerText && question?.reponseInfos && (
              <i>{question?.reponseInfos}</i>
            )}
          </p>
        )}
      </div>
      {selectAnswerButton && question?.reponsePlus && (
        <p className="text-page-semib text-page-semib--greenSmall questionsText--alignLeft">
          {question?.reponsePlus}
          {question?.reponseInfos && <i>{question?.reponseInfos}</i>}
        </p>
      )}
      {selectAnswerButton &&
        (showAnswerText || question?.choix) &&
        question?.contenuEtoile && (
          <ul>
            {question?.contenuEtoile?.map((etoile, index) => (
              <li
                key={index}
                className="text-page-light text-page-light--blackMedium questionsText__italic"
              >
                {etoile}
              </li>
            ))}
          </ul>
        )}

      <div className="questionsText__button button--animated" ref={buttonRef}>
        {!selectAnswerButton && (
          <Button
            scene="start"
            colorName="blue"
            isSelected={false}
            disabled={hasAnswered}
            onClick={() => {
              handleAnswerClick();
            }}
          >
            Voir la réponse
          </Button>
        )}

        {selectAnswerButton && (
          <>
            {showPointsCheck === "no" && (
              <div
                className="pointsCheck"
                style={{
                  position: "absolute",
                  top: `${imagePosition.top}%`,
                  left: `${imagePosition.left}%`,
                }}
              >
                <img src={unionRed} alt="union" />
                <p className="pointsCheck--red">+0 pt</p>
              </div>
            )}
            {showPointsCheck === "yes" && (
              <div
                className="pointsCheck"
                style={{
                  position: "absolute",
                  top: `${imagePosition.top}%`,
                  left: `${imagePosition.left}%`,
                }}
              >
                <img src={union} alt="union" />
                <p className="pointsCheck--blue">{`+${questionDifficulty} pt${
                  questionDifficulty > 1 ? "s" : ""
                }`}</p>
              </div>
            )}
            <p
              className={`text-infos-light checkbutton--animated ${
                showPointsCheck
                  ? "text-infos-light--white"
                  : "text-infos-light--blue"
              }`}
            >
              {`Aviez-vous juste?`}
            </p>

            <div className="CheckButtonsList checkbutton--animated">
              <Button
                scene="choices"
                colorName={showPointsCheck === "yes" ? "gray" : "red"}
                isSelected={false}
                onClick={() => handleAnswer(false)}
                animationClass={
                  hasAnswered
                    ? `${
                        showPointsCheck === "yes"
                          ? "button-border--white"
                          : "button-border--red button__choices--red--selected"
                      }`
                    : ""
                }
              >
                <FaFaceFrown />
                <p className="text-page-semib text-page-semib--whiteBig">
                  Non...
                </p>
              </Button>
              <Button
                scene="choices"
                colorName={showPointsCheck === "no" ? "gray" : "blue"}
                isSelected={false}
                onClick={() => handleAnswer(true)}
                animationClass={
                  hasAnswered
                    ? `${
                        showPointsCheck === "no"
                          ? "button-border--white"
                          : "button-border--blue button__choices--blue--selected"
                      }`
                    : ""
                }
              >
                <FaFaceSmile />
                <p className="text-page-semib text-page-semib--whiteBig">
                  Oui !
                </p>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Questions;
